import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { styled } from 'twin.macro';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { StaticDateTimePicker } from '@mui/x-date-pickers';

import Icon from '@ubisend/pulse-icons';

import {
  Input,
  Label,
  Airplane,
  Container,
  LeftSection,
  ComposerButton,
  labelAnimations
} from '../Custom/MessageComposer';
import { DateProvider } from '../../../../../Providers';
import { DateWrapper } from '../../../../../Components';
import { useComposer, useDelivery } from '../../../../../hooks';

const isValid = date => dayjs(date, 'DD-MM-YYYY HH:mm').isValid();

const CustomCalendarHeaderRoot = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 16px',
  alignItems: 'center',
});

function CustomCalendarHeader({ date, onMonthChange, onYearClick }) {
  const selectNextMonth = () => onMonthChange(dayjs(date).add(1, 'month'));
  const selectPreviousMonth = () => onMonthChange(dayjs(date).subtract(1, 'month'));

  return (
    <CustomCalendarHeaderRoot>
      <Stack spacing={1} direction="row">
        <Icon
          type="cheveronLeft"
          width="1.25rem"
          height="1.25rem"
          onClick={selectPreviousMonth}
          title="Previous month"
          aria-label="Previous month"
          role="button"
        />
      </Stack>
      <Typography
        variant="body2"
        style={{ cursor: 'pointer' }}
        onClick={onYearClick}
        aria-label="Select year"
      >
        {dayjs(date).format('MMMM YYYY')}
      </Typography>
      <Stack spacing={1} direction="row">
        <Icon
          type="cheveronRight"
          width="1.25rem"
          height="1.25rem"
          onClick={selectNextMonth}
          title="Next month"
          aria-label="Next month"
          role="button"
        />
      </Stack>
    </CustomCalendarHeaderRoot>
  );
}

CustomCalendarHeader.propTypes = {
  date: PropTypes.object.isRequired,
  onMonthChange: PropTypes.func.isRequired,
  onYearClick: PropTypes.func.isRequired,
};

const DateTimeComposer = () => {
  const [open, setOpen] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { t } = useTranslation('full_page');
  const { reply, setReply } = useComposer();
  const { sendMessage } = useDelivery();

  const handleChange = date => {
    setReply(dayjs(date).format('DD-MM-YYYY HH:mm'));
  };

  const handleInputClick = () => {
    if (!initialized && !reply) {
      setReply(dayjs().format('DD-MM-YYYY HH:mm'));
      setInitialized(true);
    }

    setOpen(prev => !prev);
  };

  const handleSend = () => {
    if (isValid(reply)) {
      sendMessage(reply);
      setOpen(false);
      setReply('');
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const value = reply ? dayjs(reply, 'DD-MM-YYYY HH:mm') : dayjs();

  return (
    <DateProvider primary="#FFFFFF">
      <DateWrapper>
        {open && (
          <StaticDateTimePicker
            open
            variant="dialog"
            TextFieldComponent={() => null}
            onAccept={date => {
              handleChange(date);
              setOpen(false);
              setShowYearPicker(false);
            }}
            onChange={handleChange}
            value={value}
            maxDate={dayjs().add(10, 'year')}
            minutesStep={1}
            localeText={{ dateTimePickerToolbarTitle: '' }}
            views={showYearPicker ? ['year'] : ['year', 'month', 'day', 'hours', 'minutes']}
            slots={{ calendarHeader: CustomCalendarHeader }}
            slotProps={{
              calendarHeader: {
                date: value,
                onYearClick: () => setShowYearPicker(prev => !prev),
                onMonthChange: newDate => handleChange(newDate),
              },
              toolbar: { hidden: false },
              tabs: { hidden: true },
              actionBar: { actions: [] },
            }}
          />
        )}
        <Container onKeyDown={handleKeyDown}>
          <LeftSection htmlFor="date-time-composer">
            <Label
              htmlFor="date-time-composer"
              initial="expanded"
              variants={labelAnimations}
              animate={open || reply ? 'minimised' : 'expanded'}
            >
              {t('date_time_composer_label')}
            </Label>
            <Input
              id="date-time-composer"
              readOnly
              tabIndex={0}
              role="textbox"
              aria-keyshortcuts="Enter"
              value={reply}
              onClick={handleInputClick}
            />
          </LeftSection>
          <ComposerButton
            aria-label={t('send_button_label')}
            onClick={handleSend}
          >
            <Airplane />
          </ComposerButton>
        </Container>
      </DateWrapper>
    </DateProvider>
  );
};

export default DateTimeComposer;